import { 
  getStorage, 
  ref, 
  uploadBytes, 
  getDownloadURL, 
  deleteObject,
  getMetadata,
  updateMetadata
} from 'firebase/storage';
import { app } from './config';
import { auth } from './firebase';
import { saveDocumentToFirestore } from './firestore';

const storage = getStorage(app);

export async function uploadFileToStorage(file: File): Promise<string> {
  if (!auth.currentUser) {
    throw new Error('No authenticated user');
  }

  try {
    // Validate file type
    const validTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/plain'
    ];

    if (!validTypes.includes(file.type)) {
      throw new Error('Invalid file type. Please upload a PDF, DOC, DOCX, or TXT file.');
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      throw new Error('File is too large. Maximum size is 10MB.');
    }

    // Create a unique filename
    const timestamp = Date.now();
    const uniqueFileName = `${timestamp}-${file.name}`;
    const filePath = `resumes/${auth.currentUser.uid}/${uniqueFileName}`;
    const storageRef = ref(storage, filePath);

    // Add metadata
    const metadata = {
      contentType: file.type,
      customMetadata: {
        originalName: file.name,
        uploadedBy: auth.currentUser.uid,
        uploadedAt: new Date().toISOString()
      }
    };

    // Upload file with metadata
    const snapshot = await uploadBytes(storageRef, file, metadata);
    
    // Get download URL
    const downloadURL = await getDownloadURL(snapshot.ref);

    // Save document metadata to Firestore
    await saveDocumentToFirestore(
      file.name,
      downloadURL,
      file.type,
      file.size
    );

    return downloadURL;
  } catch (error) {
    console.error('Error uploading file:', error);
    throw error;
  }
}

export async function deleteFileFromStorage(fileURL: string): Promise<void> {
  if (!auth.currentUser) {
    throw new Error('No authenticated user');
  }

  try {
    // Get reference from URL
    const fileRef = ref(storage, fileURL);
    
    // Check if user owns the file
    const metadata = await getMetadata(fileRef);
    if (metadata.customMetadata?.uploadedBy !== auth.currentUser.uid) {
      throw new Error('Unauthorized to delete this file');
    }

    // Delete the file
    await deleteObject(fileRef);
  } catch (error) {
    console.error('Error deleting file:', error);
    throw error;
  }
}

export async function getFileMetadata(fileURL: string) {
  try {
    const fileRef = ref(storage, fileURL);
    return await getMetadata(fileRef);
  } catch (error) {
    console.error('Error getting file metadata:', error);
    throw error;
  }
}

export async function updateFileMetadata(fileURL: string, newMetadata: any) {
  if (!auth.currentUser) {
    throw new Error('No authenticated user');
  }

  try {
    const fileRef = ref(storage, fileURL);
    await updateMetadata(fileRef, newMetadata);
  } catch (error) {
    console.error('Error updating file metadata:', error);
    throw error;
  }
}